/**
 * The languages END USERS can be served — a different axis from the languages
 * the ADMIN reads the panel in.
 *
 * `SUPPORTED_LOCALES` in ./locales.ts is the panel's own chrome: the menus,
 * buttons and table headers an operator sees. This list is the set of languages
 * a piece of *content* (a package title, a gift name, a quest description) can
 * carry. They are not the same thing and must not be merged: the panel ships
 * its chrome in tr/en while the app serves fr/de users, so collapsing the two
 * would make the panel unable to author a language it cannot display itself in.
 *
 * Mirrors shuffly's `LocaleResolver::AUTHORING_LOCALES`. Backend READS are
 * gateless — a row serves whatever locales it happens to carry — so this list
 * only governs what the admin UI offers to type. Adding a fifth language is one
 * edit here plus the matching one in shuffly.
 */
export const CONTENT_LOCALES = ['tr', 'en', 'fr', 'de'] as const;
export type ContentLocale = (typeof CONTENT_LOCALES)[number];

/** Mirrors shuffly's `LocaleResolver::DEFAULT`. */
export const CONTENT_FALLBACK_LOCALE: ContentLocale = 'en';

export interface ContentLocaleDescriptor {
  readonly code: ContentLocale;
  readonly nativeName: string;
  readonly englishName: string;
}

export const CONTENT_LOCALE_REGISTRY: Readonly<Record<ContentLocale, ContentLocaleDescriptor>> = Object.freeze({
  tr: { code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish' },
  en: { code: 'en', nativeName: 'English', englishName: 'English' },
  fr: { code: 'fr', nativeName: 'Français', englishName: 'French' },
  de: { code: 'de', nativeName: 'Deutsch', englishName: 'German' },
});

export function isContentLocale(value: string): value is ContentLocale {
  return (CONTENT_LOCALES as readonly string[]).includes(value);
}

/** A localized text column as it travels on the wire: locale code -> copy. */
export type LocalizedMap = Readonly<Record<string, string>>;

/**
 * Resolve a localized map the way the server does, so a preview built on this
 * shows what the client will actually render.
 *
 * The chain reproduces `ShopStorefrontService::resolveLocale`:
 *   requested locale -> `CONTENT_FALLBACK_LOCALE` -> first string value -> null.
 *
 * Note the deliberate fidelity: a persisted empty string at the requested
 * locale WINS, shadowing the English fallback, because the server's
 * `isset() && is_string()` is satisfied by ''. Keeping that behavior here means
 * a preview surfaces the resulting blank rather than papering over it. The
 * write path is what stops an empty string being stored — see localizedMapFrom.
 */
export function resolveContentText(map: LocalizedMap | null | undefined, locale: string): string | null {
  if (!map) return null;
  const requested = map[locale];
  if (typeof requested === 'string') return requested;
  const fallback = map[CONTENT_FALLBACK_LOCALE];
  if (typeof fallback === 'string') return fallback;
  for (const value of Object.values(map)) {
    if (typeof value === 'string') return value;
  }
  return null;
}

/**
 * Build the wire payload for a localized column from raw editor input.
 *
 * Blank and whitespace-only entries are dropped rather than sent as ''. This is
 * not tidiness: an empty string is a PRESENT key server-side, so storing one
 * shadows the English fallback and ships a blank title to that locale's users.
 * Absent is the only way to say "not translated yet".
 *
 * Iterates the DRAFT's own keys, not `CONTENT_LOCALES`. The write paths replace
 * the whole column, and a draft is seeded from the server's stored map — so a
 * locale this panel has no tab for is still in the draft, and filtering to the
 * authored set would silently delete it on the next save by any operator. The
 * lists live in two repos with nothing linking them, and shuffly widening first
 * (seed the copy, then teach the panel) is the natural order, so that skew is a
 * question of when rather than if. Unknown keys can only have come from the
 * server's own map; preserving them is strictly safer.
 */
export function localizedMapFrom(draft: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [code, raw] of Object.entries(draft)) {
    const text = (raw ?? '').trim();
    if (text !== '') out[code] = text;
  }
  return out;
}

/**
 * `localizedMapFrom` for a NULLABLE column: `null` when nothing survives.
 *
 * Which of the two a caller wants is a property of the column, not a default.
 * `shop_packages.title` is nullable, so an emptied map must become `null`. The
 * gifts drawer is the opposite and must always send `{}` — an absent key means
 * "untouched" downstream there, and omitting it made clearing a title a silent
 * no-op that took real digging to find. Call the one your column needs.
 */
export function localizedMapOrNull(draft: Readonly<Record<string, string>>): Record<string, string> | null {
  const map = localizedMapFrom(draft);
  return Object.keys(map).length > 0 ? map : null;
}
